import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, RefreshCw } from "lucide-react";
import { fetchAdminSubmissions } from "@/api/adminSubmissions";
import SubmissionCard from "@/components/admin/SubmissionCard";

const TABS = [
  { key: "pending", label: "In behandeling" },
  { key: "approved", label: "Goedgekeurd" },
  { key: "rejected", label: "Afgewezen" },
];

export const AdminSubmissionsPage = () => {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  const loadCounts = useCallback(async () => {
    try {
      const [p, a, r] = await Promise.all([
        fetchAdminSubmissions("pending"),
        fetchAdminSubmissions("approved"),
        fetchAdminSubmissions("rejected"),
      ]);
      setCounts({ pending: p.length, approved: a.length, rejected: r.length });
    } catch {
      /* silent */
    }
  }, []);

  const load = useCallback(
    async (activeTab = tab) => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAdminSubmissions(activeTab);
        setItems(data);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "Kon inzendingen niet laden. Probeer opnieuw.",
        );
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    load(tab);
    loadCounts();
  }, [tab, load, loadCounts]);

  const handleChanged = useCallback(() => {
    // Refresh alleen de counts; laat de bestaande cards in de lijst staan
    // zodat de approved/rejected banner zichtbaar blijft. De gebruiker kan
    // handmatig 'Verversen' klikken om de lijst te herladen.
    loadCounts();
  }, [loadCounts]);

  return (
    <div data-testid="admin-submissions-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#2E9CD6] mb-1">
            Review-wachtrij
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold">Inzendingen</h1>
          <p className="mt-2 text-[#3E5871] font-[Inter] max-w-2xl">
            Bekijk aanmeldingen en aanvullingen van ondernemers. Goedgekeurde
            inzendingen komen live in de gids; afgewezen wijzigen niets.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            load(tab);
            loadCounts();
          }}
          data-testid="submissions-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E4EEF5] hover:bg-[#F4F8FB] text-[#173A5E] font-[Poppins] font-semibold text-sm transition"
        >
          <RefreshCw className="w-4 h-4" />
          Verversen
        </button>
      </div>

      {/* Tabs */}
      <div
        data-testid="submissions-tabs"
        className="flex flex-wrap gap-2 mb-6"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition border ${
                active
                  ? "bg-[#173A5E] text-white border-[#173A5E]"
                  : "bg-white text-[#173A5E] border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              {t.label}
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${
                  active
                    ? "bg-white/25 text-white"
                    : "bg-[#F4F8FB] text-[#173A5E]"
                }`}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#5A7594] py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          Laden…
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420]">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="submissions-empty"
          className="text-center py-16 bg-white rounded-2xl border border-[#E4EEF5]"
        >
          <div className="w-14 h-14 rounded-full bg-[#F4F8FB] flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-6 h-6 text-[#2E9CD6]" />
          </div>
          <h3 className="text-xl font-[Poppins] font-bold text-[#173A5E] mb-2">
            Geen inzendingen in deze categorie
          </h3>
          <p className="text-[#5A7594] font-[Inter] max-w-sm mx-auto">
            {tab === "pending"
              ? "Alles is bijgewerkt. Zodra ondernemers een nieuwe inzending doen, verschijnen ze hier."
              : "Wanneer je inzendingen beoordeelt, worden ze hier zichtbaar."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((s) => (
            <SubmissionCard
              key={s.id}
              submission={s}
              onChanged={handleChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionsPage;
